<template>
  <div @click="selectedItem = null">
    <breadcrumbs home="Reconcile product inventory"/>

    <v-card flat>
      <v-form v-model="valid" ref="form">
        <v-container class="pt-4 px-6 pb-6" fluid>
          <v-row align="center" dense>
            <v-col cols="12" sm="3">
              <date-picker v-model="transaction.date" @change="refresh(products)" hide-details :disabled="readOnly"/>
            </v-col>
            <v-col cols="12" sm="3" align="center">
              <v-btn-toggle color="primary" mandatory v-model="transaction.bonded" @change="refresh(products)">
                <v-btn :value="true" small>In-bond</v-btn>
                <v-btn :value="false" small>Duty-paid</v-btn>
              </v-btn-toggle>
            </v-col>
            <v-col cols="12" sm="3" v-if="sites.length>1">
              <v-autocomplete label="Site" dense outlined :items="sites" v-model="transaction.siteId" item-text="name" item-value="id" hide-details @change="refresh(products)" :disabled="readOnly"/>
            </v-col>
            <v-col cols="6" sm="3" md="2">
              <v-switch label="Bottles only" dense hide-details class="ml-4 mt-0" v-model="transaction.bottlesOnly" @click="convertToBottles()" :disabled="readOnly"/>
            </v-col>
          </v-row>
          <v-row align="center" dense class="mt-3">
            <v-col cols="6" sm="3">
              <v-select v-model="transaction.value" label="Adjust values" outlined dense hide-details :items="['None','Automatically','As specified']"
                        append-outer-icon="mdi-help-circle" :disabled="readOnly" @change="changeValue"
                        @click:append-outer="$store.dispatch('help', {title:'Inventory reconciliation',topicId:'RgADAAAA',articleId:'RwAlAAAA',ref:'reconcileValues'})"/>
            </v-col>
            <v-col cols="12" sm="3" v-if="company.connectedToAccounting && values && transaction.value !== 'None'">
              <account-picker v-model="transaction.expenseAccountRef" label="Inventory adjustment account"
                              :accounts="accounts" :disabled="readOnly"/>
            </v-col>
            <v-col cols="9" sm="4" md="3" v-if="transaction.bonded" class="pb-1">
              <v-tooltip top max-width="300" open-delay="1000">
                <template v-slot:activator="{on}">
                  <div v-on="on">
                    <v-select v-model="transaction.dutyPaid" outlined dense hide-details label="Excise handling"
                              :items="[{value:true,text:'Duty-paid'},{value:false,text:'Inventory adjustment'}]"
                              append-outer-icon="mdi-help-circle" :disabled="readOnly"
                              @click:append-outer="$store.dispatch('help', {title:'Inventory reconciliation',topicId:'RgADAAAA',articleId:'RwAlAAAA',ref:'reconcileExcise'})"/>
                  </div>
                </template>
                <template v-if="transaction.dutyPaid">
                  Changes in inventory will be handled as duty-paid reductions with increases offseting reductions.
                  If total increases exceed total duty-paid reductions <em>when this transaction is posted</em>,
                  then the difference is attributed to inventory adjustments (line F on B262).
                </template>
                <template v-else>
                  Changes in inventory are attributed to inventory adjustments (line F on B262).
                  All adjusting entries are subject to audit.
                </template>
              </v-tooltip>
            </v-col>
            <v-col cols="6" sm="3" md="2" :offset="(!transaction.bonded ? 3 : 0) + (!company.connectedToAccounting || !values || transaction.value === 'None' ? 3 : 0 )">
              <v-switch label="Active only" dense hide-details class="mt-0 ml-4" v-model="transaction.activeOnly" :disabled="readOnly"/>
            </v-col>
          </v-row>
          <v-row v-if="$vuetify.breakpoint.mobile">
            <v-col>
              <scan-code @code="scan" :products="products"/>
            </v-col>
          </v-row>
          <v-row>
            <v-col class="pb-0">
              <outlined-block>
                <v-data-table width="100%" :height="$vuetify.breakpoint.mobile ? null : 'calc(100vh - 362px)'"
                              calculate-widths disable-filtering disable-pagination disable-sort hide-default-footer fixed-header
                              :items="productsList" item-key="key" no-data-text="None found" :group-by="lotTracking ? 'name' : null"
                              :headers="headers" class="no-sort"
                              :item-class="item => item.key=== selectedItem ? ($vuetify.theme.dark ? 'grey darken-1' : 'grey lighten-4') : ''"
                              @click.native.stop=""
                              @click:row="item => {selectedItem = item.key; $nextTick(() => this.$refs.form.validate())}">

                  <template v-slot:group.header="{headers,group,items}">
                    <td :colspan="headers.length">
                      <strong>{{group}}</strong> <v-btn x-small text color="primary" @click="addLot(items[0])" :disabled="readOnly">Add lot</v-btn>
                    </td>
                  </template>

                  <template v-slot:header.name>
                    <span class="mr-4">Product</span>
                    <scan-code @code="scan" :products="products"/>
                  </template>
                  <template v-slot:header.lot>
                    <span class="mr-4">Product / Lot</span>
                    <scan-code @code="scan" :products="products"/>
                  </template>

                  <template v-slot:header.physical>
                    Physical inventory
                    <v-tooltip bottom>
                      <template v-slot:activator="{on}">
                        <v-icon small v-on="on" @click="resetQuantities(false)">mdi-tag-off-outline</v-icon>
                      </template>
                      Set all physical quantities to zero
                    </v-tooltip>
                    <v-tooltip bottom>
                      <template v-slot:activator="{on}">
                        <v-icon small v-on="on" @click="resetQuantities(true)">mdi-refresh</v-icon>
                      </template>
                      Reset physical quantities to book quantities
                    </v-tooltip>
                  </template>

                  <template v-slot:item.lot="{item}">
                    <template v-if="item.editLot && selectedItem === item.key">
                      <v-text-field v-model="item.lot" hide-details class="text-body-2 d-inline-block" dense :rules="[fieldRules.required]" placeholder="Lot required"
                                    style="width:150px;left:50px" :disabled="readOnly" outlined/>
                      <v-icon small @click="deleteLot(item.key)" style="padding-left:50px">mdi-delete</v-icon>
                    </template>
                    <div v-else :class="{'error--text':!item.lot}" style="padding-left:50px">{{item.lot || 'Required'}}</div>
                  </template>
                  <template v-slot:item.book="{item}">
                    <div v-if="!transaction.bottlesOnly" :class="{'mt-n5':selectedItem === item.key}">
                      {{item.bookCases | number(0)}} cases
                    </div>
                    <div :class="{'mt-11':selectedItem === item.key,'mt-n5':selectedItem === item.key && transaction.bottlesOnly}">
                      {{item.bookBottles | number(0)}} bottles
                    </div>
                    <div v-if="transaction.value==='As specified'" :class="{'mt-11':selectedItem === item.key}">
                      {{item.bookValue | currency}}
                    </div>
                  </template>
                  <template v-slot:item.physical="{item}">
                    <template v-if="selectedItem === item.key">
                      <decimal-number-input v-if="!transaction.bottlesOnly" v-model="item.cases" prefix="" suffix="cases" :precision="0" :disabled="readOnly" @change="updateQuantity(item)"/>
                      <decimal-number-input v-model="item.bottles" prefix="" suffix="bottles" :precision="0" :disabled="readOnly" @change="updateQuantity(item)"/>
                      <decimal-number-input v-if="transaction.value==='As specified'" v-model="item.value" :disabled="readOnly"/>
                    </template>
                    <template v-else>
                      <div v-if="!transaction.bottlesOnly" :class="{'error--text':item.cases < 0}">
                        {{item.cases | number(0)}} cases
                      </div>
                      <div :class="{'error--text':item.bottles < 0}">
                        {{item.bottles | number(0)}} bottles
                      </div>
                      <div v-if="transaction.value==='As specified'" :class="{'error--text':item.value < 0}">{{item.value | currency}}</div>
                    </template>
                  </template>
                  <template v-slot:header.diff>
                    Adjust by
                    <v-menu :close-on-content-click="false" max-width="400">
                      <template v-slot:activator="{on}">
                        <v-icon small v-on="on">mdi-palette</v-icon>
                      </template>
                      <v-card flat>
                        <v-container>
                          <template v-for="(t,index) in threshHolds">
                            <v-row dense :key="index">
                              <v-col cols="5"><decimal-number-input hide-details prefix="" suffix="bottles" :precision="0" label="When >=" v-model="t.value" :disabled="index===3"/></v-col>
                              <v-col cols="7">
                                <v-color-picker canvas-height="50" hide-inputs flat v-model="t.color" width="300"/>
                                </v-col>
                            </v-row>
                          </template>
                        </v-container>
                      </v-card>
                    </v-menu>
                  </template>
                  <template v-slot:item.diff="{item}">
                    <div v-if="!transaction.bottlesOnly" :style="diffStyle(item)" class="font-weight-bold" :class="{'mt-n5':selectedItem === item.key}">
                      {{diff(item).cases | number(0)}} cases
                    </div>
                    <div style="diffStyle(item)" :style="diffStyle(item)" class="font-weight-bold" :class="{'mt-11':selectedItem === item.key,'mt-n5':selectedItem === item.key && transaction.bottlesOnly}">
                      {{diff(item).bottles}} bottles
                    </div>
                    <div v-if="transaction.value==='As specified'" class="font-weight-bold" :class="{'mt-11':selectedItem === item.key}">
                      {{item.value - item.bookValue | currency}}
                    </div>
                  </template>
                </v-data-table>
              </outlined-block>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" class="pb-0">
              <v-text-field label="Comments" v-model="transaction.comments" outlined dense :rules="[fieldRules.maxLength(255)]" hide-details :disabled="readOnly"/>
            </v-col>
          </v-row>
        </v-container>
      </v-form>

      <v-divider/>
      <v-card-actions class="px-6">
        <v-spacer/>
        <v-btn :plain="!readOnly" color="primary" @click="cancel">{{readOnly ? 'Done' : 'Cancel'}}</v-btn>
        <v-btn color="primary" outlined :loading="saving" @click="save(true)" v-if="!readOnly">Save as draft</v-btn>
        <v-btn color="primary" :loading="saving" @click="save(false)" v-if="!readOnly">Save</v-btn>
      </v-card-actions>
    </v-card>

    <confirm-leave v-model="next"/>
  </div>

</template>

<script>
import {format} from 'date-fns'
import confirmLeave from '@/mixins/confirmLeave'
import InBond from '@/components/InBond'

export default {
  name: 'InventoryProductsReconcileEdit',
  mixins: [confirmLeave],
  components: {InBond},
	props: {
    id: {type: String, required:false}
	},
	data() {
		return {
      valid: true,
      transaction: {products:[], bonded:true, value:'None', dutyPaid:true, date: format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
                    bottlesOnly:false, draft:false,activeOnly:false,
                    expenseAccountRef:'',comments:'Inventory reconciliation',siteId:this.$store.getters.sites(true, {B262:true})[0].id},
      saving:false,
      accounts: [],
      products: [],
      selectedItem: null,
      threshHolds: [{value:100,color:'#ff0000'},{value:30,color:'#D65A9F'},{value:6,color:'#C3C340'},{value:0,color:'#00ff00'}]
		}
  },
  computed: {
    company() {
      return this.$store.getters.company;
    },
    productsList() {
      return this.products.filter(product => this.transaction.activeOnly ? product.active : true);
    },
    sites() {
      return this.$store.getters.sites(true, this.transaction.bonded ? {B262:true} : undefined);
    },
    readOnly() {
      return !this.company.permissions.includes('inventory.write');
    },
    lotTracking() {
      return this.company.settings.lotTrackingForProducts && (this.transaction.bonded || this.company.settings.lotTrackingForDutyPaid);
    },
    headers() {
      const headers = [{text:'Product',value:'name',width:'35%',divider:true,class:'px-2',cellClass:'py-2 px-2'}];
      if (this.lotTracking)
        headers.push({text:'Product / Lot',value:'lot',width:'20%',divider:true,class:'px-2',cellClass:'py-2 px-2'});
      return [...headers, {text:'Book inventory',value:'book',width:'15%',align:'right',divider:true,class:'px-2',cellClass:'py-2 px-2'},
                          {text:'Physical inventory',value:'physical',width:'15%',align:'right',divider:true,class:'px-2',cellClass:'py-2 px-2'},
                          {text:'Adjust by',value:'diff',width:'15%',align:'right',divider:true,class:'px-2',cellClass:'py-2 px-2'}];
    },
    values() {
      return this.$store.getters.company.permissions.includes('values') && this.$store.getters.company.settings.useAccounts;
    }
  },
	async created() {
    const results = await this.$apiCall(`query InventoryProductsReconcileEdit($id:ID!, $edit:Boolean!, $accounting:Boolean!) {
                                            productReconciliation(id:$id) @include(if:$edit) {
                                              id,bonded,dutyPaid,value,date,activeOnly,comments,expenseAccountRef,bottlesOnly,site{id},draft
                                              products{id,lot,cases,bottles,value}
                                            }
                                            accounts @include(if:$accounting) {
                                              ref,parentRef,name,type,typeName
                                            }
                                            companyAccounts @skip(if:$edit) {inventoryAdjustment}
                                          }`,
                                        {id:this.id??'',edit:!!this.id,accounting:this.company.connectedToAccounting});
    if (results.error) {
      this.$nextTick(() => this.dirty = false);
      this.$router.back();
      return;
    }
    if (this.company.connectedToAccounting)
      this.accounts = results.data.accounts;
    if (this.id) {
      this.transaction = results.data.productReconciliation;
      this.transaction.siteId = this.transaction.site.id;
      delete this.transaction.site;
    }
    else
      this.transaction.expenseAccountRef = results.data.companyAccounts.inventoryAdjustment;
    await this.refresh(this.transaction.products);
    const threshHolds = localStorage.getItem(`${this.transaction.bonded?'bonded-':''}products-reconcile`);
    if (threshHolds)
      this.threshHolds = JSON.parse(threshHolds);
//    if (this.transaction.bottlesOnly)
//      this.convertToBottles();
  },
  watch: {
    threshHolds: {
      handler() {
        localStorage.setItem(`${this.transaction.bonded?'bonded-':''}products-reconcile`, JSON.stringify(this.threshHolds));
      },
      immediate: false,
      deep: true
    }
  },
	methods: {
    scan({type, id, quantity, cases, lot}) {
      if (type !== 'Product')
        return;
      lot = lot ?? '';
      let product = this.products.find(product => product.id === id && product.lot === lot);
      if (!product) {
        product = this.products.find(product => product.id === id);
        if (!product)
          return;
        product = this.addLot(product);
        product.lot = lot;
      }
      if (this.transaction.bottlesOnly && cases) {
        const productObject = this.products.find(p => p.id === id);
        product.bottles += quantity * productObject.caseSize;
      }
      else
        product[cases?'cases':'bottles'] += quantity;
    },
    resetQuantities(toBook) {
      for (const product of this.products) {
        product.bottles = toBook ? product.bookBottles : 0;
        product.cases = toBook ? product.bookCases : 0;
        product.value = toBook ? product.bookValue : 0;
      }
    },
    changeValue() {
      for (const product of this.productsList)
        this.updateQuantity(product);
    },
    updateQuantity(product) {
      if (this.transaction.value === 'As specified') {
        const bookBootles = product.bookCases * product.caseSize + product.bookBottles;
        const percent = bookBootles === 0 ? 0 : (product.cases * product.caseSize + product.bottles) / (product.bookCases * product.caseSize + product.bookBottles)
        product.value = this.$roundNumber(product.bookValue * percent);
      }
    },
    diff(product) {
      let bottles = (product.cases * product.caseSize + product.bottles) - (product.bookCases * product.caseSize + product.bookBottles);
      const neg = bottles < 0;
      bottles = Math.abs(bottles);
      let cases = this.transaction.bottlesOnly ? 0 : Math.floor(Math.abs(bottles) / product.caseSize);
      bottles -= cases * product.caseSize;
      if (neg) {
        bottles = -bottles;
        cases = -cases;
      }
      return {cases, bottles};
    },
    diffStyle(product) {
      const bottles = Math.abs((product.cases * product.caseSize + product.bottles) - (product.bookCases * product.caseSize + product.bookBottles));
      return `color:${this.threshHolds.find(t => bottles >= t.value)?.color || '#000'}`;
    },
    addLot(product) {
      let index = this.products.findIndex(p => p.id === product.id);
      while (this.products[index]?.id === product.id)
        index++;
      const lotProduct = {id:product.id,name:product.name,caseSize:product.caseSize,siteId:product.siteId,lot:'',active:product.active,
                          bookCases:0,bookBottles:0,bookValue:0,
                          cases:0,bottles:0,value:0,
                          editLot:true,key:Date.now()}
      this.products.splice(index, 0, lotProduct);
      this.selectedItem = lotProduct.key
      this.$nextTick(() => this.$refs.form.validate());
      return lotProduct;
    },
    deleteLot(key) {
      const index = this.products.findIndex(p => p.key === key);
      this.products.splice(index, 1);
    },
    async refresh(existingProducts) {
      const allProducts = [];
      await this.$lazyListLoad(allProducts, 'products2', 'id,name,upc,scc,active,caseSize,alcohol{type},sites {siteId,inBondBottles,dutyPaidBottles,inBondValue,dutyPaidValue,lot}', 100, [], [{name:'asof',value:this.transaction.date}]);

      // Expand each product to include a row for each unique site/lot
      const newProducts = [];
      let key = 1;
      const products = this.transaction.bonded ? allProducts.filter(product => product.alcohol.type !== 'NonExcise') : allProducts;
      for (const product of products) {
        let foundProductAtSite = false;
        for (const site of product.sites) {
          if (site.siteId === this.transaction.siteId) {
            // Get current book values
            const bookCases = Math.floor((this.transaction.bonded ? site.inBondBottles : site.dutyPaidBottles) / product.caseSize);
            const bookBottles = (this.transaction.bonded ? site.inBondBottles : site.dutyPaidBottles) - bookCases * product.caseSize;
            const bookValue = this.transaction.bonded ? site.inBondValue : site.dutyPaidValue;

            if (bookCases || bookBottles) {   // Add if there is inventory, otherwise ignore
              foundProductAtSite = true;
              newProducts.push({id:product.id,name:product.name,active:product.active,
                                caseSize:product.caseSize,lot:site.lot,
                                bookCases, bookBottles, bookValue,
                                cases:bookCases, bottles:bookBottles, value:bookValue,
                                siteId:site.siteId,
                                key:key++});
            }
          }
        }
        if (!foundProductAtSite) {   // No product at site, so add placeholder so product is at least in the list
          newProducts.push({id:product.id,name:product.name,caseSize:product.caseSize,lot:'L0', bookCases:0, bookBottles:0, bookValue:0,
                            cases:0, bottles:0, value:0,
                            key:key++});
          foundProductAtSite = true;
        }
      }

      // Now, update product list from existing product list
      for (const existingProduct of existingProducts) {
        let product = newProducts.find(p => p.id === existingProduct.id && p.lot === existingProduct.lot && p.siteId === this.transaction.siteId);
        const setFrom = {cases:existingProduct.cases, bottles:existingProduct.bottles, value:existingProduct.value};
        if (product)
          Object.assign(product, setFrom);
        else {
          product = allProducts.find(p => p.id === existingProduct.id);
          if (product.siteId !== this.transaction.siteId)
            continue;
          let index = newProducts.findIndex(p => p.id === product.id);
          while (newProducts[++index]?.id === product.id);
          newProducts.splice(index, 0, {id:product.id,name:product.name,caseSize:product.caseSize,lot:existingProduct.lot, bookCases:0, bookBottles:0, bookValue:0,
                                        ...setFrom, key:key++});
//          newProducts.push({id:product.id,name:product.name,caseSize:product.caseSize,lot:existingProduct.lot, bookCases:0, bookBottles:0, bookValue:0,
//                            ...setFrom, key:key++});
        }
      }

      this.products = newProducts;

      if (this.transaction.bottlesOnly)
        this.convertToBottles();
    },
    convertToBottles() {
      if (this.transaction.bottlesOnly)
        this.products.forEach(product => {
          product.bookBottles += product.bookCases * product.caseSize;
          product.bookCases = 0;
          product.bottles += product.cases * product.caseSize;
          product.cases = 0;
        });
      else
        this.products.forEach(product => {
          product.bookCases = Math.floor(product.bookBottles / product.caseSize);
          product.bookBottles -= product.bookCases * product.caseSize;
          product.cases = Math.floor(product.bottles / product.caseSize);
          product.bottles -= product.cases * product.caseSize;
        });
    },
    cancel() {
      this.$router.back();
    },
		async save(draft) {
      if (!this.valid || this.productsList.some(product => (this.lotTracking && !product.lot) || product.cases < 0 || product.bottles < 0 || product.value < 0)) {
        this.$store.commit('setError', {type:'warning',message:`One or more fields is missing or invalid`});
        return;
      }
      this.transaction.draft = draft;
      this.transaction.products = this.productsList.map(product => ({id:product.id,lot:product.lot,
                                                                cases:product.cases,bottles:product.bottles,
                                                                value:this.transaction.value==='None'?product.bookValue:(this.transaction.value==='Automatically'?null:product.value)}));
      this.saving = true;
      const results = await this.$apiCall(`mutation InventoryProductsReconcileEdit($input: ProductReconciliationInput!) {
                                              productReconciliationCrupdate(input:$input) {id}
                                            }`,
                                          {input:this.transaction});
      this.saving = false;
      if (!results.error) {
        this.dirty = false;
        this.$router.back();
        this.$store.commit('setError', {type:'success',message:`Created ${draft?'draft':''} inventory reconciliation`});
      }
		}
	}
}
</script>